// The Properties panel's own settings (issue 1575): which sections this person has opened or closed — per KIND of
// thing (a monster, a player character, scenery, the scene), never per token — and a DM's own regrouping of the
// system's stat block. Both live in the person's layout document under the table's base-system label
// (`LayoutDoc.panel`, design/07 §7; binding decisions 9, 21, 31, 38), so they follow the person to every table of the
// same system and never reach anyone else. The shell loads them with the document and saves what changes here with it;
// with no shell (a test, a panel on its own) they are this page's alone.
import { useEffect, useState } from 'react';
import { HOTBAR_DEFAULTS, type DiceSection, type HotbarPrefs, type PanelKind, type PanelPrefs, type StatBlockLayout } from '@dragons-table/vtt-shared';

type Listener = (p: PanelPrefs) => void;
let prefs: PanelPrefs = {};
const listeners = new Set<Listener>();
/** The shell's: called with the new settings on a change made here (never on a load), to keep them in the document. */
let save: ((p: PanelPrefs) => void) | null = null;

const emit = () => { for (const l of listeners) l(prefs); };

export const panelPrefs = {
  get: (): PanelPrefs => prefs,
  /** The document's settings, as the shell loaded them: nothing is saved back for a load. */
  load(next: PanelPrefs | undefined) { prefs = next ?? {}; emit(); },
  /** Where a change is kept (the shell's document); null, nowhere but here. */
  onSave(fn: ((p: PanelPrefs) => void) | null) { save = fn; },
  subscribe(l: Listener): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
  /** A section opened or closed, for this kind of thing. */
  setOpen(kind: PanelKind, section: string, open: boolean) {
    prefs = { ...prefs, open: { ...(prefs.open ?? {}), [kind]: { ...(prefs.open?.[kind] ?? {}), [section]: open } } };
    emit();
    save?.(prefs);
  },
  /** The DM's own stat-block layout, or null to go back to the system's. */
  setStatBlock(layout: StatBlockLayout | null) {
    const { statBlock: _old, ...rest } = prefs;
    prefs = layout ? { ...rest, statBlock: layout } : rest;
    emit();
    save?.(prefs);
  },
  /** Colour-match (qa-runs#362): the panel takes the selected token's marker colour. */
  setColourMatch(on: boolean) {
    prefs = { ...prefs, colourMatch: on };
    emit();
    save?.(prefs);
  },
  /** A dice bag section opened or closed (qa-runs#368). */
  setDiceOpen(section: DiceSection, open: boolean) {
    prefs = { ...prefs, dice: { ...(prefs.dice ?? {}), [section]: open } };
    emit();
    save?.(prefs);
  },
  /** The hot bar's settings (qa-runs#369): the fields given change; null forgets them all (Reset). */
  setHotbar(change: Partial<HotbarPrefs> | null) {
    const { hotbar: old, ...rest } = prefs;
    prefs = change === null ? rest : { ...rest, hotbar: { ...(old ?? {}), ...change } };
    emit();
    save?.(prefs);
  },
  /** Forget everything (tests). */
  reset() { prefs = {}; save = null; emit(); },
};

/** The settings, kept current. */
export function usePanelPrefs(): PanelPrefs {
  const [p, setP] = useState(panelPrefs.get());
  useEffect(() => { setP(panelPrefs.get()); return panelPrefs.subscribe(setP); }, []);
  return p;
}

/** Whether a section is open for this kind of thing: as this person last left it, else its default. */
export function useSectionOpen(kind: PanelKind, section: string, fallback: boolean): [boolean, (open: boolean) => void] {
  const p = usePanelPrefs();
  const stored = p.open?.[kind]?.[section];
  return [stored ?? fallback, (open: boolean) => panelPrefs.setOpen(kind, section, open)];
}

/** The kind a thing's sections are remembered under: a player character, scenery (its layer, or a prop), else a monster. */
export function panelKindOf(o: { kind: string; pc?: boolean; layer?: string }): PanelKind {
  if (o.pc) return 'pc';
  if (o.kind === 'prop' || o.layer === 'scenery') return 'scenery';
  return 'monster';
}

/** Whether a dice bag section is open (qa-runs#368): as this person last left it, else open. */
export function useDiceSectionOpen(section: DiceSection): [boolean, (open: boolean) => void] {
  const p = usePanelPrefs();
  return [p.dice?.[section] ?? true, (open: boolean) => panelPrefs.setDiceOpen(section, open)];
}

export type HotbarLook = Required<Omit<HotbarPrefs, 'count'>> & { count: number | null };
/** The hot bar's settings (qa-runs#369): this person's, each unset one at Tom's default. */
export function useHotbarPrefs(): HotbarLook {
  const p = usePanelPrefs();
  return { ...HOTBAR_DEFAULTS, ...(p.hotbar ?? {}) } as HotbarLook;
}
